<template>
  <div
    class="program-price-layout"
    :class="{ 'program-price-layout--inactive': !isActive }"
    data-testid="program-price-estimate"
  >
    <div class="program-cost-field">
      <slot name="cost" />
    </div>

    <template v-if="isActive">
      <div class="processing-fee-field">
        <label :for="estimatedProcessingFeeId">
          {{ t("Phrases.Participation.ProgramPriceEstimate.EstimatedProcessingFee") }}
        </label>
        <div class="input-group">
          <span class="input-group-text">{{ currency }}</span>
          <input
            :id="estimatedProcessingFeeId"
            class="form-control"
            type="text"
            :value="formatAmount(estimate?.estimatedProcessingFee)"
            readonly
          />
        </div>
        <p class="mb-0 mt-1 small text-muted">
          {{ t("Phrases.Participation.ProgramPriceEstimate.AmountPaidByClub") }}
        </p>
      </div>

      <div class="club-payout border bg-light p-3">
        <h6>{{ t("Phrases.Participation.ProgramPriceEstimate.ClubPayout") }}</h6>
        <div class="club-payout-details">
          <div>
            <div class="small text-muted">
              {{ t("Phrases.Participation.ProgramPriceEstimate.AfterProcessingFee") }}
            </div>
            <strong class="club-payout-amount">{{ formatMoney(estimate?.netVenueAmount) }}</strong>
          </div>
          <p class="club-payout-summary mb-0 small">
            {{ t("Phrases.Participation.ProgramPriceEstimate.ParticipantPays", {
              advertisedPrice: formatMoney(estimate?.advertisedPrice),
            }) }}<br />
            {{ t("Phrases.Participation.ProgramPriceEstimate.ClubReceives", {
              netVenueAmount: formatMoney(estimate?.netVenueAmount),
              estimatedProcessingFee: formatMoney(estimate?.estimatedProcessingFee),
            }) }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useId } from "vue";
import { useI18n } from "vue-i18n";
import type { IProgramPriceEstimate } from "@/types";

const props = defineProps<{
  currency: string;
  estimate: IProgramPriceEstimate | null;
  isActive: boolean;
}>();

const { t } = useI18n();
const estimatedProcessingFeeId = `estimated-processing-fee-${useId()}`;
const formatAmount = (value?: number) => (value ?? 0).toFixed(2);
const formatMoney = (value?: number) => `${props.currency}${formatAmount(value)}`;
</script>

<style scoped>
.program-price-layout {
  display: grid;
  gap: 1rem;
  grid-template-areas:
    "cost fee"
    "payout payout";
  grid-template-columns: minmax(0, 3fr) minmax(12rem, 1.25fr);
}

.program-price-layout--inactive {
  grid-template-areas: "cost";
  grid-template-columns: minmax(0, 1fr);
}

.program-cost-field {
  grid-area: cost;
}

.processing-fee-field {
  grid-area: fee;
}

.club-payout {
  grid-area: payout;
}

.club-payout h6 {
  margin-bottom: 0.75rem;
}

.club-payout-details {
  align-items: end;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.club-payout-amount {
  color: var(--bs-primary);
  font-size: 1.5rem;
}

.club-payout-summary {
  max-width: 28rem;
  text-align: right;
}

@media (max-width: 767.98px) {
  .program-price-layout {
    grid-template-areas:
      "cost"
      "fee"
      "payout";
    grid-template-columns: minmax(0, 1fr);
  }

  .club-payout-details {
    align-items: start;
    flex-direction: column;
  }

  .club-payout-summary {
    max-width: none;
    text-align: left;
  }
}
</style>
